import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { LIVE_CONNECTING, LIVE_OPEN, openLiveConnection, type LiveConnection } from '../application/liveConnection';
import { withLogWsTicket } from '../application/liveWs';













export type LogWsMessage = { type?: string; [key: string]: unknown };
export type LogWsHandler = (msg: LogWsMessage) => void;

const URL_PATH = '/api/logs/ws';
const PING_MS = 25_000;
const RECONNECT_MS = 3_000;
const CONNECT_TIMEOUT_MS = 8_000;
const RELEASE_GRACE_MS = 400;

let socket: LiveConnection | null = null;
let connected = false;
let wanted = 0;
let reconnectTimer: number | undefined;
let pingTimer: number | undefined;
let connectWatch: number | undefined;
let releaseTimer: number | undefined;

const handlers = new Map<string, Set<LogWsHandler>>();
const stateListeners = new Set<() => void>();

function emitState(): void {
  for (const l of stateListeners) l();
}

function setConnected(next: boolean): void {
  if (connected === next) return;
  connected = next;
  emitState();
}

function dispatch(msg: LogWsMessage): void {
  const type = typeof msg.type === 'string' ? msg.type : '';
  const typed = handlers.get(type);
  if (typed) for (const h of [...typed]) h(msg);
  const all = handlers.get('*');
  if (all) for (const h of [...all]) h(msg);
}

function wsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}${URL_PATH}`;
}

function clearTimers(): void {
  window.clearTimeout(reconnectTimer);
  window.clearTimeout(connectWatch);
  window.clearInterval(pingTimer);
  reconnectTimer = undefined;
  connectWatch = undefined;
  pingTimer = undefined;
}

function scheduleReconnect(): void {
  if (wanted <= 0) return;
  window.clearTimeout(reconnectTimer);
  reconnectTimer = window.setTimeout(connect, RECONNECT_MS);
}

function connect(): void {
  if (wanted <= 0) return;
  if (socket && (socket.readyState === LIVE_OPEN || socket.readyState === LIVE_CONNECTING)) return;
  void withLogWsTicket(wsUrl()).then((url) => {
    if (wanted <= 0) return;
    if (socket && (socket.readyState === LIVE_OPEN || socket.readyState === LIVE_CONNECTING)) return;
    openSocket(url);
  });
}

function openSocket(url: string): void {
  let ws: LiveConnection;
  try {
    ws = openLiveConnection(url);
  } catch {
    setConnected(false);
    scheduleReconnect();
    return;
  }
  socket = ws;
  let opened = false;
  connectWatch = window.setTimeout(() => {
    if (!opened && ws.readyState === LIVE_CONNECTING) {
      try {
        ws.close();
      } catch {

      }
    }
  }, CONNECT_TIMEOUT_MS);

  ws.onopen = () => {
    opened = true;
    window.clearTimeout(connectWatch);
    setConnected(true);
    window.clearInterval(pingTimer);
    pingTimer = window.setInterval(() => {
      if (socket?.readyState !== LIVE_OPEN) return;
      try {
        socket.send(JSON.stringify({ type: 'ping', ts: Date.now() }));
      } catch {

      }
    }, PING_MS);
  };

  ws.onmessage = (ev) => {
    let msg: LogWsMessage | null;
    try {
      msg = JSON.parse(String(ev.data)) as LogWsMessage | null;
    } catch {
      return;
    }
    if (!msg || typeof msg !== 'object') return;
    setConnected(true);
    if (msg.type === 'ping' || msg.type === 'pong' || msg.type === 'hello') {
      if (msg.type === 'ping' && ws.readyState === LIVE_OPEN) {
        try {
          ws.send(JSON.stringify({ type: 'pong', ts: Date.now() }));
        } catch {

        }
      }
      return;
    }
    dispatch(msg);
  };

  ws.onclose = () => {
    window.clearTimeout(connectWatch);
    window.clearInterval(pingTimer);
    if (socket === ws) socket = null;
    setConnected(false);
    scheduleReconnect();
  };

  ws.onerror = () => undefined;
}

function disconnect(): void {
  clearTimers();
  const ws = socket;
  socket = null;
  if (ws) {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    try {
      ws.close();
    } catch {

    }
  }
  setConnected(false);
}


function acquire(): () => void {
  wanted += 1;
  window.clearTimeout(releaseTimer);
  releaseTimer = undefined;
  connect();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    wanted = Math.max(0, wanted - 1);
    if (wanted > 0) return;

    window.clearTimeout(releaseTimer);
    releaseTimer = window.setTimeout(() => {
      releaseTimer = undefined;
      if (wanted === 0) disconnect();
    }, RELEASE_GRACE_MS);
  };
}


export function subscribeLogWs(type: string, handler: LogWsHandler): () => void {
  let set = handlers.get(type);
  if (!set) {
    set = new Set();
    handlers.set(type, set);
  }
  set.add(handler);
  return () => {
    set?.delete(handler);
    if (set && set.size === 0) handlers.delete(type);
  };
}

function subscribeState(fn: () => void): () => void {
  stateListeners.add(fn);
  return () => {
    stateListeners.delete(fn);
  };
}

const getConnected = (): boolean => connected;


export function useLogWsConnected(): boolean {
  return useSyncExternalStore(subscribeState, getConnected, getConnected);
}

type UseLogWsOptions = {

  enabled?: boolean;
};





export function useLogWs(opts: UseLogWsOptions = {}): { connected: boolean } {
  const { enabled = true } = opts;
  const isOpen = useLogWsConnected();
  useEffect(() => {
    if (!enabled) return undefined;
    return acquire();
  }, [enabled]);
  return { connected: isOpen };
}


export function useLogWsMessage(type: string, handler: LogWsHandler, enabled = true): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!enabled) return undefined;
    return subscribeLogWs(type, (msg) => ref.current(msg));
  }, [type, enabled]);
}


export function LogWsProvider({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
  useLogWs({ enabled });
  return <>{children}</>;
}
